/**
 * Product Canvas - Canvas-based panning gallery with irregular product positioning
 * Supports mouse drag panning and touch gestures
 */

class ProductCanvas {
  constructor(viewport) {
    this.viewport = viewport;
    this.wrapper = viewport.querySelector('.product-canvas-wrapper');
    this.canvas = viewport.querySelector('[data-canvas-content]');
    this.list = viewport.querySelector('.product-canvas__list');
    if (!this.wrapper || !this.canvas || !this.list) return;

    this.isDown = false;
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.offsetX = 0;
    this.offsetY = 0;
    this.velocityX = 0;
    this.velocityY = 0;
    this.lastX = 0;
    this.lastY = 0;
    this.lastTime = 0;
    this.animationFrame = null;

    this.init();
  }

  init() {
    // Position products irregularly
    this.positionProducts();

    // Mouse events
    this.viewport.addEventListener('mousedown', this.handleMouseDown.bind(this));
    this.viewport.addEventListener('mouseleave', this.handleMouseUp.bind(this));
    this.viewport.addEventListener('mouseup', this.handleMouseUp.bind(this));
    this.viewport.addEventListener('mousemove', this.handleMouseMove.bind(this));

    // Touch events
    this.viewport.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
    this.viewport.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.viewport.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });

    // Prevent text selection while dragging
    this.viewport.addEventListener('selectstart', (e) => {
      if (this.isDown) e.preventDefault();
    });

    // Prevent context menu
    this.viewport.addEventListener('contextmenu', (e) => {
      if (this.isDown) e.preventDefault();
    });

    // Smooth momentum animation
    this.animate();

    // Handle window resize
    window.addEventListener('resize', () => {
      this.constrainPan();
    });
  }

  positionProducts() {
    const items = this.list.querySelectorAll('.product-canvas__item');
    if (items.length === 0) return;

    // Wait for canvas to be properly sized
    setTimeout(() => {
      const canvasWidth = this.wrapper.offsetWidth || window.innerWidth * 2;
      const canvasHeight = this.wrapper.offsetHeight || window.innerHeight * 2;
      
      // Smaller sizes like lusano.com reference
      const itemWidth = window.innerWidth > 749 ? 220 : 160;
      const itemHeight = window.innerWidth > 749 ? 280 : 200;
      const minSpacing = 120; // Minimum space between items
      const padding = 100;
      
      // Calculate grid layout with more columns
      const colsPerRow = Math.floor((canvasWidth - padding * 2) / (itemWidth + minSpacing));
      
      // Create organic/irregular positioning with NO overlap
      const usedAreas = [];

      items.forEach((item, index) => {
        let attempts = 0;
        let x, y;
        let validPosition = false;

        // Try to find a valid position that doesn't overlap
        while (!validPosition && attempts < 100) {
          // Create organic spacing - not perfectly aligned
          const row = Math.floor(index / colsPerRow);
          const col = index % colsPerRow;
          
          // Base position with proper spacing
          const baseX = padding + (col * (itemWidth + minSpacing));
          const baseY = padding + (row * (itemHeight + minSpacing));
          
          // Add randomness for organic feel but less aggressive
          const randomOffsetX = (Math.random() - 0.5) * 80;
          const randomOffsetY = (Math.random() - 0.5) * 60;
          
          x = baseX + randomOffsetX;
          y = baseY + randomOffsetY;

          // Ensure within canvas bounds
          x = Math.max(padding, Math.min(x, canvasWidth - itemWidth - padding));
          y = Math.max(padding, Math.min(y, canvasHeight - itemHeight - padding));

          // Check for overlap - ensure NO overlap
          validPosition = true;
          for (const used of usedAreas) {
            // Check if boxes overlap
            const overlapX = x < used.x + used.width + 40 && x + itemWidth + 40 > used.x;
            const overlapY = y < used.y + used.height + 40 && y + itemHeight + 40 > used.y;
            
            if (overlapX && overlapY) {
              validPosition = false;
              break;
            }
          }

          attempts++;
        }

        // Set position with will-change for performance
        item.style.left = `${Math.round(x)}px`;
        item.style.top = `${Math.round(y)}px`;
        item.style.willChange = 'transform';

        // Store used area with dimensions
        usedAreas.push({ x, y, width: itemWidth, height: itemHeight });
      });
    }, 100);
  }

  handleMouseDown(e) {
    this.isDown = true;
    this.viewport.style.cursor = 'grabbing';
    
    const rect = this.viewport.getBoundingClientRect();
    this.startX = e.clientX - rect.left;
    this.startY = e.clientY - rect.top;
    
    // Get current transform from wrapper
    const transform = this.getTransform();
    this.offsetX = transform.x;
    this.offsetY = transform.y;
    
    this.velocityX = 0;
    this.velocityY = 0;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.lastTime = Date.now();
  }

  handleMouseUp() {
    if (!this.isDown) return;
    this.isDown = false;
    this.viewport.style.cursor = 'grab';
    this.constrainPan();
  }

  handleMouseMove(e) {
    if (!this.isDown) return;
    e.preventDefault();

    const rect = this.viewport.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;

    const deltaX = currentX - this.startX;
    const deltaY = currentY - this.startY;

    this.currentX = this.offsetX + deltaX;
    this.currentY = this.offsetY + deltaY;

    this.updateTransform();

    // Calculate velocity for momentum
    const now = Date.now();
    const timeDelta = now - this.lastTime;
    if (timeDelta > 0) {
      this.velocityX = (e.clientX - this.lastX) / timeDelta;
      this.velocityY = (e.clientY - this.lastY) / timeDelta;
    }
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.lastTime = now;
  }

  handleTouchStart(e) {
    if (e.touches.length !== 1) return;
    this.isDown = true;
    
    const touch = e.touches[0];
    const rect = this.viewport.getBoundingClientRect();
    this.startX = touch.clientX - rect.left;
    this.startY = touch.clientY - rect.top;
    
    const transform = this.getTransform();
    this.offsetX = transform.x;
    this.offsetY = transform.y;
    
    this.velocityX = 0;
    this.velocityY = 0;
    this.lastX = touch.clientX;
    this.lastY = touch.clientY;
    this.lastTime = Date.now();
  }

  handleTouchMove(e) {
    if (!this.isDown || e.touches.length !== 1) return;
    e.preventDefault();

    const touch = e.touches[0];
    const rect = this.viewport.getBoundingClientRect();
    const currentX = touch.clientX - rect.left;
    const currentY = touch.clientY - rect.top;

    const deltaX = currentX - this.startX;
    const deltaY = currentY - this.startY;

    this.currentX = this.offsetX + deltaX;
    this.currentY = this.offsetY + deltaY;

    this.updateTransform();

    // Calculate velocity
    const now = Date.now();
    const timeDelta = now - this.lastTime;
    if (timeDelta > 0) {
      this.velocityX = (touch.clientX - this.lastX) / timeDelta;
      this.velocityY = (touch.clientY - this.lastY) / timeDelta;
    }
    this.lastX = touch.clientX;
    this.lastY = touch.clientY;
    this.lastTime = now;
  }

  handleTouchEnd() {
    if (!this.isDown) return;
    this.isDown = false;
    this.constrainPan();
  }

  getTransform() {
    const style = window.getComputedStyle(this.wrapper);
    const matrix = style.transform || style.webkitTransform;
    
    if (matrix === 'none') {
      return { x: 0, y: 0 };
    }

    const matrixType = matrix.includes('3d') ? '3d' : '2d';
    const matrixValues = matrix.match(/matrix.*\((.+)\)/)[1].split(', ');
    
    if (matrixType === '2d') {
      return {
        x: parseFloat(matrixValues[4]) || 0,
        y: parseFloat(matrixValues[5]) || 0
      };
    } else {
      return {
        x: parseFloat(matrixValues[12]) || 0,
        y: parseFloat(matrixValues[13]) || 0
      };
    }
  }

  updateTransform() {
    // Use translate3d for GPU acceleration - move the wrapper so grid moves too
    this.wrapper.style.transform = `translate3d(${Math.round(this.currentX)}px, ${Math.round(this.currentY)}px, 0)`;
  }

  constrainPan() {
    const viewportRect = this.viewport.getBoundingClientRect();
    const wrapperRect = this.wrapper.getBoundingClientRect();
    
    const maxX = 0;
    const minX = viewportRect.width - wrapperRect.width;
    const maxY = 0;
    const minY = viewportRect.height - wrapperRect.height;

    this.currentX = Math.max(minX, Math.min(maxX, this.currentX));
    this.currentY = Math.max(minY, Math.min(maxY, this.currentY));
    
    this.updateTransform();
  }

  animate() {
    // Apply momentum scrolling when not dragging
    if (!this.isDown && (Math.abs(this.velocityX) > 0.1 || Math.abs(this.velocityY) > 0.1)) {
      this.currentX += this.velocityX * 10;
      this.currentY += this.velocityY * 10;
      
      this.velocityX *= 0.95; // Friction
      this.velocityY *= 0.95;
      
      this.constrainPan();
    } else if (!this.isDown) {
      this.velocityX = 0;
      this.velocityY = 0;
    }

    this.animationFrame = requestAnimationFrame(() => this.animate());
  }

  destroy() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
  }
}

// Initialize all product canvases on the page
document.addEventListener('DOMContentLoaded', () => {
  const canvasViewports = document.querySelectorAll('.product-canvas-viewport');
  const canvases = [];

  canvasViewports.forEach(viewport => {
    canvases.push(new ProductCanvas(viewport));
  });

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    canvases.forEach(canvas => canvas.destroy());
  });
});

// Also initialize for dynamically loaded content
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const canvasViewports = document.querySelectorAll('.product-canvas-viewport');
    canvasViewports.forEach(viewport => {
      new ProductCanvas(viewport);
    });
  });
} else {
  const canvasViewports = document.querySelectorAll('.product-canvas-viewport');
  canvasViewports.forEach(viewport => {
    new ProductCanvas(viewport);
  });
}

